/** Shortest password the store accepts (the server's rules: apps/server/src/lib/passwords.ts). */
export const PASSWORD_MIN_LENGTH = 8;

/** Help-center articles that hold the legal documents (linked from sign-up and the footer). */
export const LEGAL_ARTICLES = {
  terms: 'terms-of-sale',
  license: 'license-agreement',
  privacy: 'privacy',
} as const;

export const BRAND = {
  company: 'Visual Artform',
  store: 'VA Plugin Store',
  handle: '@va.plugin',
  instagram: 'https://www.instagram.com/va.plugin/',
  colors: {
    dark: '#111226',
    lift: '#1b1c3c',
    panel: '#161838',
    indigo: '#2b2e9f',
    indigoLight: '#3539b0',
    accent: '#a8d0f8',
    headline: '#f3f8ff',
    body: '#dbe9fb',
    muted: '#8b93c6',
    dim: '#646a96',
  },
} as const;

/**
 * Links that open the desktop app at a page: vaplugin://library, vaplugin://checkout/complete?order=...
 * The app registers it (apps/desktop/src/main/config.ts has its own copy: keep them the same).
 */
export const APP_PROTOCOL = 'vaplugin';

export const ROLES = ['customer', 'team', 'admin'] as const;
/** deleted: in Admin > Users > Deleted, can't sign in, can be restored. */
export const USER_STATUSES = ['active', 'suspended', 'deleted'] as const;

export const PRODUCT_CATEGORIES = ['ffgl-effect', 'ffgl-source', 'ffgl-mixer', 'wire', 'app', 'bundle'] as const;
export const PRODUCT_CATEGORY_LABELS: Record<(typeof PRODUCT_CATEGORIES)[number], string> = {
  'ffgl-effect': 'Effect',
  'ffgl-source': 'Source',
  'ffgl-mixer': 'Mixer',
  wire: 'Wire Patch',
  app: 'App',
  bundle: 'Bundle',
};
export const PRODUCT_STATUSES = ['draft', 'published', 'archived'] as const;
export const DEMO_MODES = ['watermark', 'none'] as const;

export const PLATFORMS = ['win', 'mac'] as const;
export const PLATFORM_LABELS: Record<(typeof PLATFORMS)[number], string> = { win: 'Windows', mac: 'macOS' };

/** Platform a build was compiled for. `any` = platform-independent (e.g. Wire patches). */
export const BUILD_PLATFORMS = ['win-x64', 'win-arm64', 'mac-universal', 'mac-arm64', 'mac-x64', 'any'] as const;

/**
 * How the desktop app installs a build. Every product gets its own folder, never loose files:
 *  - ffgl:      zip (or single .dll/.bundle) extracted into <Extra Effects>/<Product>/
 *  - ffgl-app:  plugin + app in one zip: its "Extra Effects/" folder (or a .dll/.bundle at the
 *               top of the zip) goes to <Extra Effects>/<Product>/, its "App/" folder and
 *               everything else to <Downloads>/<Product>/
 *  - wire:      zip/patch copied into the Resolume Wire patches folder (FFGL binaries in it go
 *               to <Extra Effects>/<Product>/)
 *  - app-zip:   zip extracted into <Downloads>/<Product>/ (standalone app)
 *  - installer: an .exe/.msi/.pkg/.dmg saved to <Downloads>/<Product>/ and opened
 *  - files:     zip extracted into a folder the user picks
 */
export const INSTALL_TYPES = ['ffgl', 'ffgl-app', 'wire', 'app-zip', 'installer', 'files'] as const;

export const ENTITLEMENT_SOURCES = ['purchase', 'bundle', 'team', 'gift', 'admin', 'trial'] as const;
export const ENTITLEMENT_SOURCE_LABELS: Record<(typeof ENTITLEMENT_SOURCES)[number], string> = {
  purchase: 'Purchased',
  bundle: 'Bundle',
  team: 'Team license',
  gift: 'Gift',
  admin: 'Granted',
  trial: 'Trial',
};

export const ORDER_STATUSES = ['pending', 'paid', 'failed', 'canceled', 'refunded'] as const;
export const PAYMENT_PROVIDERS = ['stripe', 'paypal', 'free', 'manual', 'mock'] as const;
export const CODE_KINDS = ['team', 'gift', 'promo'] as const;

export const TICKET_STATUSES = ['open', 'pending', 'closed'] as const;
export const TICKET_CATEGORIES = ['licensing', 'install', 'billing', 'bug', 'feature', 'other'] as const;

/** Log files customers can attach to support messages: plain-text logs only (.log is the Mac's usual). */
export const SUPPORT_LOG_EXTENSIONS = ['.txt', '.log'] as const;
export const SUPPORT_LOG_MAX_BYTES = 4 * 1024 * 1024;
export const SUPPORT_LOG_MAX_FILES = 3;
export const TICKET_CATEGORY_LABELS: Record<(typeof TICKET_CATEGORIES)[number], string> = {
  licensing: 'Licensing & activation',
  install: 'Installing & updates',
  billing: 'Billing & orders',
  bug: 'Bug report',
  feature: 'Feature request',
  other: 'Something else',
};

/** Known host applications a product can run in (shown on product pages). */
export const KNOWN_HOSTS = [
  'Resolume Arena 7.x',
  'Resolume Avenue 7.x',
  'Resolume Wire',
  'Any FFGL 2.x host',
  'Standalone',
] as const;
